import {
  DownloadOutlined,
  DownOutlined,
  FileOutlined,
  RightOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Row,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import HeaderPage from 'common/components/HeaderPage';
import { getClientByIdService, getClientSubscriptionByIdService } from 'common/services/extends/client';
import {
  ClientDataDetailTypes, ClientSubscription, ClientTransactions, FileUploadTypes
} from 'common/services/extends/client/types';

const { Title, Text } = Typography;

interface ActiveRoles {
  roleIndex: boolean;
}

const ClientDetail: React.FC<ActiveRoles> = ({ roleIndex }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const idParams = Number(searchParams.get('id'));
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(new Set());
  const { data: res, isLoading } = useQuery(
    ['getClientDataById', idParams],
    () => (idParams ? getClientByIdService(idParams) : undefined),
    { enabled: roleIndex && !!idParams }
  );

  const { data: subscriptionData, isLoading: subscriptionDataLoading } = useQuery(
    ['getClientSubscription', idParams],
    () => {
      if (idParams) {
        return getClientSubscriptionByIdService(idParams);
      }
      return undefined;
    },
    {
      enabled: roleIndex && !!idParams,
    }
  );

  const paymentMethods = [
    {
      value: 1, label: `${t('client.oneTime')}`
    },
    { value: 2, label: `${t('client.multiple')}` },
  ];

  const data: ClientDataDetailTypes | undefined = res;

  const client = data?.clientData;
  const creator = data?.creator;
  const updater = data?.updater;

  // Subscription Status Tag
  const getSubscriptionStatus = (status: number) => {
    const map: Record<number, { color: string; label: string }> = {
      1: { color: 'default', label: 'N/A' },
      2: { color: 'default', label: 'N/A' },
      3: { color: 'success', label: t('client.statusSubscription.active') },
      4: { color: 'volcano', label: t('client.statusSubscription.cancel') },
      5: { color: 'processing', label: t('client.statusSubscription.pending') },
      6: { color: 'error', label: t('client.statusSubscription.expired') },
      7: { color: 'default', label: t('client.statusSubscription.inactive') },
    };

    const { color, label } = map[status] || { color: 'default', label: t('client.statusSubscription.unknown') };
    return <Tag color={color}>{label}</Tag>;
  };

  // Transaction Status Tag
  const getTransactionStatus = (status: number) => {
    const map: Record<number, { color: string; label: string }> = {
      1: { color: 'default', label: t('client.statusTransaction.init') },
      2: { color: 'error', label: t('client.statusTransaction.failed') },
      3: { color: 'success', label: t('client.statusTransaction.success') },
    };

    const { color, label } = map[status] || { color: 'default', label: t('client.statusTransaction.unknown') };
    return <Tag color={color}>{label}</Tag>;
  };

  const handleExpand = (recordId: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(recordId)) {
      newExpanded.delete(recordId);
    } else {
      newExpanded.add(recordId);
    }
    setExpandedRows(newExpanded);
  };

  const subscriptionColumns = [
    {
      title: t('subscription.id'),
      key: 'orderCode',
      render: (_: any, record: ClientSubscription) => (
        <Space>
          {expandedRows.has(record.id) ? (
            <DownOutlined style={{ fontSize: 12 }} />
          ) : (
            <RightOutlined style={{ fontSize: 12 }} />
          )}
          <Typography.Text>{record.orderCode || record.code}</Typography.Text>
        </Space>
      ),
    },
    {
      title: t('subscription.package'),
      dataIndex: 'packageName',
      key: 'packageName',
    },
    {
      title: t('subscription.createdDate'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: t('system.endDate'),
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => (date ? dayjs(date ?? '').format('DD/MM/YYYY') : 'N/A'),
    },
    {
      title: t('subscription.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => getSubscriptionStatus(status),
    },
  ];

  const transactionColumns = [
    {
      title: t('transaction.id'),
      key: 'code',
      render: (_: any, tx: ClientTransactions) => (
        <Space>
          <Text>{tx.code}</Text>
        </Space>
      ),
    },
    {
      title: t('transaction.dueDate'),
      dataIndex: 'reqPaymentDate',
      key: 'reqPaymentDate',
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: t('transaction.amount'),
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => (
        <Typography.Text>
          {Number(amount).toLocaleString()}
          {' '}
          VND
        </Typography.Text>
      ),
    },
    {
      title: t('transaction.paymentTime'),
      key: 'paymentTime',
      render: (_: any, tx: ClientTransactions) => {
        if (!tx.actPaymentDate) return '-';
        const date = dayjs(tx.actPaymentDate);
        return (
          <Space direction="vertical" size={0}>
            <Typography.Text>
              {date.format('HH:mm, DD MMM YYYY')}
            </Typography.Text>
          </Space>
        );
      },
    },
    {
      title: t('transaction.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => getTransactionStatus(status),
    },
  ];

  const expandedRowRender = (record: ClientSubscription) => (
    <div style={{
      margin: '0 48px', background: '#fafafa', padding: 16, borderRadius: 8
    }}
    >
      <Typography.Text strong style={{ display: 'block', marginBottom: 12 }}>
        {t('subscription.history')}
      </Typography.Text>
      <Table
        columns={transactionColumns}
        dataSource={record.transactions}
        pagination={false}
        size="small"
        rowKey="id"
      />
    </div>
  );

  // Helper: render file list
  const renderContractFiles = (files: FileUploadTypes[]) => {
    if (!files?.length) return <Text type="secondary">No file</Text>;

    return (
      <Space direction="vertical" size="small">
        {files.map((file) => (
          <div key={file.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileOutlined />
            <a href={file.path} target="_blank" rel="noopener noreferrer">
              {file.name}
            </a>
            <Button
              type="link"
              size="small"
              icon={<DownloadOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                window.open(file.path, '_blank');
              }}
            />
          </div>
        ))}
      </Space>
    );
  };

  // Status tag
  const getStatusTag = (status: number) => {
    const isActive = status === 2;
    return (
      <Tag color={isActive ? 'green' : 'red'} style={{ margin: 0 }}>
        {isActive ? t('client.active') : t('client.inactive')}
      </Tag>
    );
  };

  return (
    <div>
      <HeaderPage
        fixed
        title={t('client.detail')}
      />

      <Spin spinning={isLoading}>
        <div className="t-mainlayout_wrapper" style={{ padding: '24px' }}>
          {/* General Information */}
          <Card style={{ marginBottom: 24 }}>
            <Title level={5}>
              <Text strong>{t('client.generalInformation')}</Text>
            </Title>
            <Divider style={{ margin: '12px 0' }} />

            <Row gutter={[32, 16]}>
              <Col span={8}>
                <Descriptions column={1} colon={false}>
                  <Descriptions.Item label={t('client.clientId')}>
                    <Text strong>{client?.code || '-'}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('client.companyName')}>
                    {client?.name || '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              <Col span={8}>
                <Descriptions column={1} colon={false}>
                  <Descriptions.Item label={t('client.status')}>
                    {client ? getStatusTag(client.activityStatus) : '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('client.taxCode')}>
                    {client?.taxCode || '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Col>

              <Col span={8}>
                <Descriptions column={1} colon={false}>
                  <Descriptions.Item label={t('client.servicePackage')}>
                    {data?.servicePackageData?.name || '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>
          </Card>

          {/* System Information */}
          <Card style={{ marginBottom: 24 }}>
            <Title level={5}>
              <Text strong>{t('client.systemInformation')}</Text>
            </Title>
            <Divider style={{ margin: '12px 0' }} />

            <Row gutter={32}>
              <Col span={12}>
                <Text strong type="secondary">
                  {t('client.createdBy')}
                  {': '}
                  {creator?.name || '-'}
                </Text>
                <br />
                <Text type="secondary">
                  {client?.createdAt
                    ? dayjs(client.createdAt).format('HH:mm, DD MMM YYYY')
                    : '-'}
                </Text>
              </Col>
              <Col span={12}>
                <Text strong type="secondary">
                  {t('client.lastModifiedBy')}
                  {': '}
                  {updater?.name || '-'}
                </Text>
                <br />
                <Text type="secondary">
                  {client?.updatedAt
                    ? dayjs(client.updatedAt).format('HH:mm, DD MMM YYYY')
                    : '-'}
                </Text>
              </Col>
            </Row>
          </Card>

          {/* Contact Detail */}
          <Card style={{ marginBottom: 24 }}>
            <Title level={5}>
              <Text strong>{t('client.contactDetail')}</Text>
            </Title>
            <Divider style={{ margin: '12px 0' }} />

            <Row gutter={32}>
              {/* Corporate Account */}
              <Col span={12}>
                <Card size="small" title={t('client.corporationAccount')} style={{ height: '100%' }}>
                  <Descriptions column={1} colon={false}>
                    <Descriptions.Item label={t('system.name')}>
                      {client?.corporateAccountName || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('system.email')}>
                      {client?.corporateAccountMail || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('system.phone')}>
                      {client?.corporateAccountPhone || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>

              {/* Technical Support */}
              <Col span={12}>
                <Card size="small" title={t('client.technicalSupport')} style={{ height: '100%' }}>
                  <Descriptions column={1} colon={false}>
                    <Descriptions.Item label={t('system.name')}>
                      {client?.technicalSupportName || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('system.email')}>
                      {client?.technicalSupportMail || '-'}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('system.phone')}>
                      {client?.technicalSupportPhone || '-'}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </Col>
            </Row>
          </Card>

          {/* Contract Detail */}
          <Card>
            <Title level={5}>
              <Text strong>{t('client.contractDetail')}</Text>
            </Title>
            <Divider style={{ margin: '12px 0' }} />

            <Row gutter={[32, 16]}>
              <Col span={8}>
                <Descriptions column={1} colon={false}>
                  <Descriptions.Item label={t('client.contractNumber')}>
                    <Text strong>{client?.contractNumber || '-'}</Text>
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={8}>
                <Descriptions column={1} colon={false}>
                  <Descriptions.Item label={t('system.startDate')}>
                    {client?.startDate
                      ? dayjs(client.startDate).format('DD/MM/YYYY')
                      : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
              <Col span={8}>
                <Descriptions column={1} colon={false}>
                  <Descriptions.Item label={t('system.endDate')}>
                    {client?.endDate
                      ? dayjs(client.endDate).format('DD/MM/YYYY')
                      : '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Col>
            </Row>

            <Divider style={{ margin: '16px 0 8px' }} />
            <Text strong>{t('client.contractFiles')}</Text>
            <div style={{ marginTop: 8 }}>
              {client?.contractFiles
                ? renderContractFiles(client.contractFiles)
                : <Text type="secondary">No file</Text>}
            </div>
          </Card>
          <div style={{ padding: '24px', background: '#fff', borderRadius: 8 }}>
            <Text strong style={{ fontSize: 18, display: 'block', marginBottom: 24 }}>
              Subscription history
            </Text>
            <Text style={{ marginBottom: '12px' }}>
              {paymentMethods.find((item) => item.value === data?.transMethod)?.label || '-'}
            </Text>

            <Table
              columns={subscriptionColumns}
              expandable={{
                expandedRowRender,
                expandedRowKeys: Array.from(expandedRows),
                onExpand: (_, record) => handleExpand(record.id),
                expandIcon: () => null,
              }}
              loading={subscriptionDataLoading}
              dataSource={subscriptionData}
              pagination={false}
              rowKey="id"
              onRow={(record) => ({
                onClick: () => handleExpand(record.id),
                style: { cursor: 'pointer' },
              })}
            />
          </div>
        </div>
      </Spin>
    </div>
  );
};

export default ClientDetail;
